import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { EmailField } from "@RHCommerceDev/component-standard-fields";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  emailAtom,
  isTokenExpirationError,
  useSetAuthenticationDialogCurrentScreenAtom
} from "@RHCommerceDev/hooks/atoms";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import { getGlobal } from "@RHCommerceDev/utils/Globals/getGlobal";
import { useStytch } from "@stytch/react";
import { useResetPasswordTemplate } from "@RHCommerceDev/hooks/useResetPasswordTemplate";
import { useAtomValue, useSetAtom } from "jotai";
import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import memoize from "utils/memoize";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { getClientOrigin } from "@RHCommerceDev/utils/getClientOrigin";
import { useHistory } from "react-router";
import { getLocaleFromPath } from "@RHCommerceDev/utils/intlUtils";

const env = getGlobal("_env");

const ResetPasswordByEmail = () => {
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const form = useForm({
    defaultValues: {
      email: ""
    }
  });
  const stytchClient = useStytch();
  const templateId = useResetPasswordTemplate();
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const setEmailValue = useSetAtom(emailAtom);
  const { location } = useHistory();
  const { locale } = getLocaleFromPath(location?.pathname) || {
    locale: "/us/en/"
  };
  const isTokenExpirationErrors = useAtomValue(isTokenExpirationError);
  const hostName = processEnvServer
    ? getClientOrigin()
    : window?.location?.origin;

  const handleSignInWithASecureLink = React.useCallback(() => {
    setAuthenticationDialogCurrentScreen("SignInWithaSecureLink");
  }, [setAuthenticationDialogCurrentScreen]);

  const { run, loading } = useAsync(async ({ email }) => {
    try {
      setEmailValue(email);
      await stytchClient.passwords.resetByEmailStart({
        email,
        reset_password_redirect_url: `${hostName}${locale}`,
        reset_password_expiration_minutes: 15,
        reset_password_template_id: templateId,
        login_redirect_url: `${hostName}${locale}`
      });

      setAuthenticationDialogCurrentScreen("CheckYourInbox"); // Navigate to Check Your Inbox screen
    } catch (error) {
      console.error(error);
    }
  });

  const emailValue = useWatch({ control: form.control, name: "email" });
  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  const onSubmit = React.useCallback(
    values => {
      return run(values);
    },
    [run]
  );

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center">
        <div>
          <Typography className="text-transform: flex h-6 w-full items-center justify-center font-primary-rh-thin text-xl font-[75] uppercase leading-6 tracking-[-0.4px]">
            {stytchData?.RESET_YOUR_PASSWORD}
          </Typography>
          {isTokenExpirationErrors === true ? (
            <Typography className="mt-6 text-center font-primary-rhlight">
              {stytchData?.your_password_expired}
            </Typography>
          ) : (
            <Typography
              className={`mt-6 text-center font-primary-rhlight text-[11px]  ${
                isTokenExpirationErrors
                  ? "mb-6 customXs:mb-4"
                  : "my-6 customXs:my-4"
              } `}
            >
              {stytchData?.Link_to_reset_password}
            </Typography>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-y-3">
        <FormProvider {...form}>
          <form
            className="flex w-80 flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <EmailField
              label="Email"
              className={`!mt-0 ${
                emailValue
                  ? "!border-[0.5px] [&>div>fieldset]:!border-black"
                  : ""
              }`}
            />
            <div className="align-center flex justify-center">
              <Button
                type="submit"
                variant="secondary"
                color="primary"
                disabled={loading || !isFormComplete}
                className="align-center mt-7 h-[48px] w-[272px] justify-center !font-primary-rhlight text-[11px] font-[300] leading-[13.2px] tracking-[0.44px]"
              >
                {stytchData?.CONTINUE}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
      <div className="flex content-between items-center">
        <Button
          variant="link"
          className="mt-4 h-[14px] w-max p-0 !font-light decoration-[0.5px] !underline-offset-[3px]"
          onClick={handleSignInWithASecureLink}
          onKeyDown={e => {
            // Ensure the button itself is focused before handling the keydown event
            if (
              e.currentTarget === document.activeElement &&
              (e.key === "Enter" || e.key === " ")
            ) {
              e.preventDefault(); // Prevent default behavior
              handleSignInWithASecureLink();
            }
          }}
          tabIndex={0}
        >
          <Typography
            variant="caption"
            className="font-feature-settings text-wrap font-primary-rhlight text-[11px] !font-[300] leading-[13.2px] tracking-[0.44px] sm:text-nowrap "
          >
            {stytchData?.SIGN_IN_WITH}
          </Typography>
        </Button>
      </div>
    </div>
  );
};

export default memoize(ResetPasswordByEmail);
